// Basic
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import PropTypes from 'prop-types';
// Store & actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
import actionsSims from '../../../../api/actionsSims';
// Libs
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import Select from '@/common-utils/components/ui/select';
import SearchInputField from '@/common-utils/components/ui/SearchInputField';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/common-utils/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/common-utils/components/ui/radio-group';
import { Label } from '@/common-utils/components/ui/label';
import { Input } from '@/common-utils/components/ui/input';
// Common
import getTranslatedText from '@/common-utils/functions/getTranslatedText';
// Lang
import { nsSimCard } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.setFormData
 * @param root0.defaultValues
 * @param root0.organizationName
 * @returns {JSX} FormAddress
 */
function FormAddress({ organizationName, setFormData, defaultValues }) {

    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const { legalName, legalInfos } = rootOrganization;

    // Constants
    const civilityOptions = [
        { value: 'MR', name: getTranslatedText('civility.mr') },
        { value: 'MRS', name: getTranslatedText('civility.mrs') },
        { value: 'MISS', name: getTranslatedText('civility.miss') }
    ];
    const townsPostCodeWithBourough = ['75000', '13000', '69000'];
    const isCurrentAddressAvailable = rootOrganization && legalInfos;

    // State
    const [useCurrentAddress, setUseCurrentAddress] = useState(isCurrentAddressAvailable);

    // Form
    const formSchema = z.object({
        companyName: z.string().min(1, ''),
        civility: z.string().nonempty(''),
        contactSurname: z.string().min(1, ''),
        contactName: z.string().min(1, ''),
        phoneNumber: z.string().regex(/^0[1-9]\d{8}$/, ''),
        postCode: z.string().regex(/^\d{5}$/, ''),
        city: z.string().nonempty(''),
        streetNumber: z.string().optional(),
        address: z.string().nonempty(''),
        additionalAddress: z.string().optional()
    });
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            companyName: '',
            civility: '',
            contactSurname: '',
            contactName: '',
            phoneNumber: '',
            postCode: '',
            city: '',
            streetNumber: '',
            address: '',
            additionalAddress: '',
            ...defaultValues
        }
    });

    // Form values
    const values = useWatch({ control: form.control });
    const isValid = form.formState.isValid;
    const postCode = useWatch({ control: form.control, name: 'postCode' });
    const address = useWatch({ control: form.control, name: 'address' });
    const city = useWatch({ control: form.control, name: 'city' });

    // Fetch cities when post code is valid
    // ApiCalls
    const { data: dataCities, refetch: refetchGetCity } = actionsSims.getCity(postCode, false);
    const { data: dataAddress, refetch: refetchGetAddress } = actionsSims.getAddress(city.split('-')[0], '', postCode, address, false);

    // Functions
    const getBoroughMessage = () => {
        return `${getTranslatedText('modal.order_sim.form.borough_needed', nsSimCard)} ${postCode.substring(0, 4)}1)`;
    };

    // Constants
    const isBoroughTown = postCode && townsPostCodeWithBourough.includes(postCode);

    // Hooks
    // Set global form data on address option change
    useEffect(() => {
        if (useCurrentAddress) {
            form.setValue('companyName', legalName);
            form.setValue('additionalAddress', '');
            form.setValue('postCode', legalInfos.postalCode);
            form.setValue('city', legalInfos.cityName);

            if (legalInfos.normalizedAddressL4) {
                form.setValue('address', legalInfos.normalizedAddressL4);
            } else if (isBoroughTown) {
                form.setValue('address', getBoroughMessage());
            }
            else {
                form.setValue('address', '');
            }
        }
        else {
            form.setValue('companyName', '');
            form.setValue('address', '');
            form.setValue('additionalAddress', '');
            form.setValue('postCode', '');
            form.setValue('city', '');
        }
    }, [useCurrentAddress]);

    // Fetch Cities when postal code field is valid
    useEffect(() => {
        form.trigger();
        const code = form.getValues('postCode');

        if (code && !useCurrentAddress) {
            // Trigger validation for 'postCode' field
            form.trigger('postCode').then((isValidField) => {
                // Set city and address fields to empty whenever postal code is changed
                form.setValue('city', '');
                form.setValue('address', '');
                // Refetch Cities
                if (isValidField) {
                    refetchGetCity();
                }
            });
        }
    }, [postCode]);

    // Fetch addresses when city field is valid
    useEffect(() => {
        const timeout = setTimeout(() => {
            const addressContent = form.getValues('address');

            if (addressContent?.length > 2 && !useCurrentAddress) {
                form.trigger('address').then((isValidField) => {
                    // Refetch address
                    if (isValidField) {
                        refetchGetAddress();
                    }
                });
            }
        }, 300);

        return () => clearTimeout(timeout);
    }, [address, city]);

    // Set form data
    useEffect(() => {
        form.trigger();
        if (isValid) {
            setFormData({
                ...values,
                city: form.getValues('city').split('-')[0],
                isValid
            });
        }
        else {
            setFormData({ isValid });
        }
    }, [values, isValid]);

    // Functions
    const getCitiesOptions = () => {
        if (dataCities) {
            return dataCities.map((item) => { return { value: `${item.cityName}-${item.cityCode}`, name: item.cityName }; });
        }
        return [];
    };
    const getAddressOptions = () => {
        if (dataAddress) {
            return dataAddress.map((item) => { return { value: item, name: item }; });
        }
        return [];
    };

    // Constants
    const isValidAddress = !form.getFieldState('address').invalid && !form.getFieldState('postCode').invalid && !form.getFieldState('city').invalid;

    return (
        <div className='space-y-3'>
            <RadioGroup
                value={useCurrentAddress ? 'default' : 'comfortable'}
                onValueChange={(value) => setUseCurrentAddress(value === 'default')}
                className='flex justify-center xs:gap-32 sm:gap-64 mb-6'
            >
                <div className='flex items-center gap-3'>
                    <RadioGroupItem value='default' id='id_my_address' disabled={!isCurrentAddressAvailable} />
                    <Label htmlFor='id_my_address'>
                        <Entity entity='modal.order_sim.my_address' namespace={nsSimCard} />
                    </Label>
                </div>
                <div className='flex items-center gap-3'>
                    <RadioGroupItem value='comfortable' id='id_other_address' />
                    <Label htmlFor='id_other_address'>
                        <Entity entity='modal.order_sim.other_address' namespace={nsSimCard} />
                    </Label>
                </div>
            </RadioGroup>
            <Form {...form}>
                {!useCurrentAddress &&
                    <div className='grid grid-cols-[30%_70%] items-center'>
                        <FormLabel required htmlFor='companyName' className='block text-sm font-medium text-gray-700'>
                            <Entity entity='modal.order_sim.form.company' namespace={nsSimCard} />
                        </FormLabel>
                        <FormField
                            name='companyName'
                            control={form.control}
                            render={({ field }) => (
                                <FormItem className='addresses-list-input sm:w-[80%]'>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            autoFocus={!useCurrentAddress}
                                            type='text'
                                            autoComplete='off'
                                            value={field.value}
                                            onChange={(e) => field.onChange(e.target.value)}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                }
                <div className='grid grid-cols-[30%_70%] items-center'>
                    <FormLabel required htmlFor='civility' className='block text-sm font-medium text-gray-700'>
                        <Entity entity='modal.order_sim.form.civility' namespace={nsSimCard} />
                    </FormLabel>
                    <FormField
                        name='civility'
                        control={form.control}
                        render={({ field }) => (
                            <FormItem className='addresses-list-input sm:w-[20%]'>
                                <FormControl>
                                    <Select
                                        {...field}
                                        placeholder='--'
                                        options={civilityOptions}
                                        namespace={nsSimCard}
                                        value={field.value}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                <div className='grid grid-cols-[30%_70%] items-center'>
                    <FormLabel required htmlFor='contactSurname' className='block text-sm font-medium text-gray-700'>
                        <Entity entity='modal.order_sim.form.contact_surname' namespace={nsSimCard} />
                    </FormLabel>
                    <FormField
                        name='contactSurname'
                        control={form.control}
                        render={({ field }) => (
                            <FormItem className='addresses-list-input  sm:w-[80%]'>
                                <FormControl>
                                    <Input
                                        {...field}
                                        autoFocus
                                        type='text'
                                        autoComplete='off'
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                <div className='grid grid-cols-[30%_70%] items-center'>
                    <FormLabel required htmlFor='contactName' className='block text-sm font-medium text-gray-700'>
                        <Entity entity='modal.order_sim.form.contact_name' namespace={nsSimCard} />
                    </FormLabel>
                    <FormField
                        name='contactName'
                        control={form.control}
                        render={({ field }) => (
                            <FormItem className='addresses-list-input  sm:w-[80%]'>
                                <FormControl>
                                    <Input
                                        {...field}
                                        type='text'
                                        autoComplete='off'
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                <div className='grid grid-cols-[30%_70%] items-center'>
                    <FormLabel required htmlFor='phoneNumber' className='block text-sm font-medium text-gray-700'>
                        <Entity entity='modal.order_sim.form.phone_number' namespace={nsSimCard} />
                    </FormLabel>
                    <FormField
                        name='phoneNumber'
                        control={form.control}
                        render={({ field }) => (
                            <FormItem className='addresses-list-input  sm:w-[60%]'>
                                <FormControl>
                                    <Input
                                        {...field}
                                        type='number'
                                        autoComplete='off'
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                {!useCurrentAddress &&
                    <>
                        <div className='grid grid-cols-[30%_70%] items-center'>
                            <FormLabel required htmlFor='postCode' className='block text-sm font-medium text-gray-700'>
                                <Entity entity='modal.order_sim.form.post_code' namespace={nsSimCard} />
                            </FormLabel>
                            <FormField
                                name='postCode'
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem className='addresses-list-input sm:w-[30%]'>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                type='text'
                                                autoComplete='off'
                                                value={field.value}
                                                onChange={(e) => field.onChange(e.target.value)}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className='grid grid-cols-[30%_70%] items-center'>
                            <FormLabel required htmlFor='city' className='block text-sm font-medium text-gray-700'>
                                <Entity entity='modal.order_sim.form.city' namespace={nsSimCard} />
                            </FormLabel>
                            <FormField
                                name='city'
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem className='addresses-list-input sm:w-[60%]'>
                                        <FormControl>
                                            <Select
                                                {...field}
                                                placeholder='--'
                                                options={getCitiesOptions()}
                                                namespace={nsSimCard}
                                                value={field.value}
                                                disabled={form.getFieldState('postCode').invalid}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className='grid grid-cols-[30%_70%] items-center'>
                            <FormLabel htmlFor='streetNumber' className='block text-sm font-medium text-gray-700'>
                                <Entity entity='modal.order_sim.form.street_number' namespace={nsSimCard} />
                            </FormLabel>
                            <FormField
                                name='streetNumber'
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem className='addresses-list-input sm:w-[30%]'>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                type='text'
                                                autoComplete='off'
                                                value={field.value}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                disabled={form.getFieldState('city').invalid || form.getFieldState('postCode').invalid}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className='grid grid-cols-[30%_70%] items-center'>
                            <FormLabel required htmlFor='address' className='block text-sm font-medium text-gray-700'>
                                <Entity entity='modal.order_sim.form.address' namespace={nsSimCard} />
                            </FormLabel>
                            <FormField
                                name='address'
                                control={form.control}
                                render={
                                    ({ field }) => {
                                        let addressValue = '';

                                        if (field.value) { addressValue = field.value; }
                                        else if (isBoroughTown) { getBoroughMessage(); }

                                        return <FormItem className='addresses-list-input sm:w-[70%]'>
                                            <FormControl>
                                                <SearchInputField
                                                    {...field}
                                                    type='searchInput'
                                                    options={getAddressOptions()}
                                                    value={addressValue}
                                                    onChange={(value) => field.onChange(value)}
                                                    disabled={form.getFieldState('city').invalid || form.getFieldState('postCode').invalid || isBoroughTown}
                                                    autoComplete='off'
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>;
                                    }
                                }
                            />
                        </div>
                        <div className='grid grid-cols-[30%_70%] items-center'>
                            <FormLabel htmlFor='additionalAddress' className='block text-sm font-medium text-gray-700'>
                                <Entity entity='modal.order_sim.form.additional_address' namespace={nsSimCard} />
                            </FormLabel>
                            <FormField
                                name='additionalAddress'
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem className='addresses-list-input sm:w-[70%]'>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                type='text'
                                                autoComplete='off'
                                                value={field.value}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                disabled={form.getFieldState('city').invalid
                                                    || form.getFieldState('postCode').invalid
                                                    || form.getFieldState('address').invalid
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </>
                }
                {(useCurrentAddress || isValidAddress) &&
                    (
                        <div className='form-card-wrapper p-4 text-center'>
                            <span>{organizationName}</span>
                            <span>{form.getValues('compnayName')}</span>
                            <span>{form.getValues('contactName')}</span>
                            <div>
                                <span>
                                    {form.getValues('address')}
                                    {form.getValues('additionalAddress')
                                        ? ` ${form.getValues('additionalAddress')}, `
                                        : ', '}
                                </span>
                                <span>{form.getValues('postCode')}, </span>
                                <span>{form.getValues('city').split('-')[0]}</span>
                            </div>
                        </div>
                    )
                }
            </Form>
        </div>
    );
}

FormAddress.propTypes = {
    setFormData: PropTypes.func,
    defaultValues: PropTypes.object,
    organizationName: PropTypes.string
};
export default FormAddress;